import { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, serverTimestamp, updateDoc } from 'firebase/firestore';
import { API_BASE, INDUSTRY_VOICES_THEME, isFallbackImageUrl } from '../../config';
import { db } from '../../firebase';
import {
  autoFormatMarkdownFromPlainText,
  splitOneLineIntoTitleDekBody,
} from '../../utils/autoFormatMarkdown';
import AdminSlotReviewTool from './AdminSlotReviewTool';
import { uploadPostImageWithThumbnail } from '../../utils/uploadImageToStorage';

function looksLikeHtml(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  return /<\/?[a-z][\s\S]*>/i.test(s);
}

function parseAiResponseToPostFields(rawContent, fallbackTitle) {
  const raw = String(rawContent || '').trim();
  if (!raw) {
    return {
      title: fallbackTitle || 'Untitled iGaming update',
      summary: '',
      body: '',
    };
  }

  const text = raw.replace(/\r\n/g, '\n');
  const lines = text.split('\n');
  const nonEmptyIdx = lines.map((l, i) => ({ l, i })).filter((x) => String(x.l).trim());

  let titleLine = '';
  let summary = '';
  let body = '';

  // Label-aware parsing while preserving the body formatting.
  const findLabelLine = (re) => lines.findIndex((l) => re.test(String(l || '').trim()));
  const titleLabelIdx = findLabelLine(/^title[:-]*$/i);
  const dekLabelIdx = findLabelLine(/^dek[:-]*$/i);
  const bodyLabelIdx = findLabelLine(/^body[:-]*$/i);

  if (titleLabelIdx !== -1 && dekLabelIdx !== -1) {
    titleLine = String(lines[titleLabelIdx + 1] || '').trim();
    summary = String(lines[dekLabelIdx + 1] || '').trim();
    const start =
      bodyLabelIdx !== -1 ? bodyLabelIdx + 1 : Math.max(dekLabelIdx + 2, titleLabelIdx + 2);
    body = lines.slice(start).join('\n').trim();
  } else if (nonEmptyIdx.length >= 2) {
    titleLine = String(nonEmptyIdx[0].l || '').trim();
    summary = String(nonEmptyIdx[1].l || '').trim();
    body = lines.slice(nonEmptyIdx[1].i + 1).join('\n').trim();
  } else {
    // One-line output: attempt to split title/dek/body heuristically.
    const guess = splitOneLineIntoTitleDekBody(text);
    titleLine = guess.title || '';
    summary = guess.dek || '';
    body = guess.body || text;
  }

  // If the "title" itself looks like HTML, the model likely returned HTML-only.
  // In that case treat the entire raw as body and use fallback title.
  if (looksLikeHtml(titleLine) && !String(body || '').trim()) {
    return {
      title: fallbackTitle || 'Untitled iGaming update',
      summary: '',
      body: raw,
    };
  }

  const title = (titleLine || fallbackTitle || 'Untitled iGaming update').replace(/^Title:\s*/i, '');
  let finalBody = body;

  // Fail-safe: never store an empty body if we got *any* content.
  if (!finalBody) {
    // If the remaining raw (minus title/summary) isn't available, store raw.
    finalBody = raw;
  }

  finalBody = autoFormatMarkdownFromPlainText(finalBody);
  return { title, summary, body: finalBody };
}

function AdminContentCreation() {
  const [sourceUrl, setSourceUrl] = useState('');
  const [aiStatus, setAiStatus] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [createdSlug, setCreatedSlug] = useState('');
  const [postImageFile, setPostImageFile] = useState(null);
  const [postImageUploadPct, setPostImageUploadPct] = useState(0);

  const [voicesText, setVoicesText] = useState('');
  const [voicesImageUrl, setVoicesImageUrl] = useState('');
  const [voicesStatus, setVoicesStatus] = useState('');
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesCreatedSlug, setVoicesCreatedSlug] = useState('');
  const [voicesImageFile, setVoicesImageFile] = useState(null);
  const [voicesImageUploadPct, setVoicesImageUploadPct] = useState(0);

  const validateImageFile = (file) => {
    if (!file) return null;
    const maxBytes = 8 * 1024 * 1024; // 8MB
    if (file.size > maxBytes) return 'Image is too large (max 8MB).';
    if (!String(file.type || '').startsWith('image/')) return 'Only image files are supported.';
    return null;
  };

  const handleGenerateAiPost = async () => {
    if (!API_BASE) {
      setAiStatus('API base URL is not configured. Set REACT_APP_API_BASE_URL in your env.');
      return;
    }

    setAiLoading(true);
    setAiStatus('Requesting AI-generated article…');
    setCreatedSlug('');
    setPostImageUploadPct(0);

    const fileError = validateImageFile(postImageFile);
    if (fileError) {
      setAiLoading(false);
      setAiStatus(fileError);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceUrl: sourceUrl || undefined }),
      });

      if (!response.ok) throw new Error('AI API returned non-200 status');

      const data = await response.json();
      const raw = data.content || '';
      const fetchedImageUrl = data.imageUrl || '';
      const meta = data.meta || {};
      const parsed = parseAiResponseToPostFields(raw, 'Untitled iGaming update');
      const title = parsed.title;
      const summary = parsed.summary;
      const body = parsed.body;

      const baseSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const slug = `${baseSlug || 'igaming-update'}-${Date.now().toString(36)}`;

      const words = (body || '').split(/\s+/).filter(Boolean).length;
      const readMinutes = Math.max(2, Math.round(words / 200));

      const fetchedIsFallback = !postImageFile && isFallbackImageUrl(fetchedImageUrl);
      const shouldPublishInitially = !postImageFile && !fetchedIsFallback;

      const docRef = await addDoc(collection(db, 'posts'), {
        slug,
        category: meta.category || 'Market Stats',
        region: meta.region || 'Global',
        title,
        excerpt: summary || String(body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 220),
        body: body || '',
        kpi: '',
        sentiment: 'Neutral',
        readMinutes,
        themes: Array.isArray(meta.themes) ? meta.themes : [],
        imageUrl: postImageFile ? null : fetchedImageUrl || null,
        published: shouldPublishInitially,
        views: 0,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sourceType: sourceUrl ? 'ai-from-url' : 'ai',
        sourceUrl: sourceUrl || null,
      });

      if (postImageFile) {
        setAiStatus('Uploading image to Firebase Storage…');
        const uploaded = await uploadPostImageWithThumbnail({
          file: postImageFile,
          folder: `posts/${docRef.id}`,
          onProgress: setPostImageUploadPct,
        });
        await updateDoc(docRef, {
          imageUrl: uploaded.imageUrl,
          imageThumbUrl: uploaded.imageThumbUrl,
          imageLqip: uploaded.imageLqip || null,
          published: true,
          updatedAt: serverTimestamp(),
        });
      }

      setAiStatus('AI article generated and stored in Firestore.');
      setCreatedSlug(slug);
      setPostImageFile(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error generating AI post', err);
      setAiStatus('Failed to generate or store AI article. Check console and API logs.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateIndustryVoices = async () => {
    if (!API_BASE) {
      setVoicesStatus('API base URL is not configured. Set REACT_APP_API_BASE_URL in your env.');
      return;
    }

    const raw = String(voicesText || '').trim();
    if (raw.length < 40) {
      setVoicesStatus('Text is required (min 40 characters).');
      return;
    }

    setVoicesLoading(true);
    setVoicesStatus('Requesting Industry Voices article…');
    setVoicesCreatedSlug('');
    setVoicesImageUploadPct(0);

    const voicesFileError = validateImageFile(voicesImageFile);
    if (voicesFileError) {
      setVoicesLoading(false);
      setVoicesStatus(voicesFileError);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/ai/industry-voices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: raw,
          imageUrl: voicesImageUrl || undefined,
        }),
      });

      if (!response.ok) throw new Error('Industry Voices API returned non-200 status');

      const data = await response.json();
      const content = data.content || '';
      const fetchedImageUrl = data.imageUrl || '';
      const parsed = parseAiResponseToPostFields(content, 'Industry voices update');
      const title = parsed.title;
      const summary = parsed.summary;
      const body = parsed.body;

      const baseSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const slug = `${baseSlug || 'industry-voices'}-${Date.now().toString(36)}`;

      const words = (body || '').split(/\s+/).filter(Boolean).length;
      const readMinutes = Math.max(2, Math.round(words / 200));

      const fetchedIsFallback = !voicesImageFile && isFallbackImageUrl(voicesImageUrl || fetchedImageUrl);
      const shouldPublishInitially = !voicesImageFile && !fetchedIsFallback;

      const docRef = await addDoc(collection(db, 'posts'), {
        slug,
        category: 'Industry Voices',
        region: 'Global',
        title,
        excerpt: summary || String(body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 220),
        body: body || '',
        kpi: '',
        sentiment: 'Neutral',
        readMinutes,
        themes: [INDUSTRY_VOICES_THEME],
        imageUrl: voicesImageFile ? null : voicesImageUrl || fetchedImageUrl || null,
        published: shouldPublishInitially,
        views: 0,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sourceType: 'ai-industry-voices',
        sourceUrl: null,
      });

      if (voicesImageFile) {
        setVoicesStatus('Uploading image to Firebase Storage…');
        const uploaded = await uploadPostImageWithThumbnail({
          file: voicesImageFile,
          folder: `posts/${docRef.id}`,
          onProgress: setVoicesImageUploadPct,
        });
        await updateDoc(docRef, {
          imageUrl: uploaded.imageUrl,
          imageThumbUrl: uploaded.imageThumbUrl,
          imageLqip: uploaded.imageLqip || null,
          published: true,
          updatedAt: serverTimestamp(),
        });
      }

      setVoicesStatus('Industry Voices post generated and stored in Firestore.');
      setVoicesCreatedSlug(slug);
      setVoicesText('');
      // keep image url (often reused), don’t clear it
      setVoicesImageFile(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error generating Industry Voices post', err);
      setVoicesStatus('Failed to generate or store Industry Voices post. Check console and API logs.');
    } finally {
      setVoicesLoading(false);
    }
  };

  return (
    <div className="admin-tab">
      <details className="admin-collapsible" open>
        <summary className="admin-collapsible-summary">
          <span>News creation</span>
          <span className="secondary-text">Generate a new post from a URL</span>
        </summary>
        <div className="admin-collapsible-body">
          <div className="mini-list admin-form">
            <div className="mini-item">
              <label className="mini-label" htmlFor="sourceUrl">
                Source article URL (optional)
              </label>
              <input
                id="sourceUrl"
                className="text-input"
                type="url"
                placeholder="Paste a public iGaming news URL to rewrite"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </div>
            <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
              <label className="mini-label" htmlFor="postImageUpload">
                Upload image (optional)
              </label>
              <input
                id="postImageUpload"
                className="text-input"
                type="file"
                accept="image/*"
                disabled={aiLoading}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setPostImageFile(f);
                }}
              />
              {postImageFile && (
                <div className="secondary-text" style={{ marginTop: 6 }}>
                  Selected: <strong>{postImageFile.name}</strong>
                  {postImageUploadPct > 0 ? ` · Upload ${postImageUploadPct}%` : ''}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="cta-button"
              onClick={handleGenerateAiPost}
              disabled={aiLoading}
            >
              <span>{aiLoading ? '…' : '+'}</span> {aiLoading ? 'Generating…' : 'Generate AI post'}
            </button>
            {createdSlug && (
              <Link to={`/news/${createdSlug}`} className="badge-pill">
                View latest AI post
              </Link>
            )}
          </div>

          {aiStatus && (
            <p className="secondary-text" style={{ marginTop: 10 }}>
              {aiStatus}
            </p>
          )}
        </div>
      </details>

      <div style={{ height: 16 }} />

      <details className="admin-collapsible" open>
        <summary className="admin-collapsible-summary">
          <span>Industry Voices creation</span>
          <span className="secondary-text">Generate an influencer-style post from raw text</span>
        </summary>
        <div className="admin-collapsible-body">
          <div className="mini-list admin-form">
            <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
              <label className="mini-label" htmlFor="voicesText">
                Text (notes / transcript / social post)
              </label>
              <textarea
                id="voicesText"
                className="text-input"
                rows={6}
                placeholder="Paste raw notes and the AI will generate the title + article body…"
                value={voicesText}
                onChange={(e) => setVoicesText(e.target.value)}
              />
            </div>
            <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
              <label className="mini-label" htmlFor="voicesImageUrl">
                Image URL (optional)
              </label>
              <input
                id="voicesImageUrl"
                className="text-input"
                type="url"
                placeholder="https://…"
                value={voicesImageUrl}
                onChange={(e) => setVoicesImageUrl(e.target.value)}
              />
            </div>
            <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
              <label className="mini-label" htmlFor="voicesImageUpload">
                Upload image (optional)
              </label>
              <input
                id="voicesImageUpload"
                className="text-input"
                type="file"
                accept="image/*"
                disabled={voicesLoading}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setVoicesImageFile(f);
                }}
              />
              {voicesImageFile && (
                <div className="secondary-text" style={{ marginTop: 6 }}>
                  Selected: <strong>{voicesImageFile.name}</strong>
                  {voicesImageUploadPct > 0 ? ` · Upload ${voicesImageUploadPct}%` : ''}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="cta-button"
              onClick={handleGenerateIndustryVoices}
              disabled={voicesLoading}
            >
              <span>{voicesLoading ? '…' : '+'}</span>{' '}
              {voicesLoading ? 'Generating…' : 'Generate Industry Voices post'}
            </button>
            {voicesCreatedSlug && (
              <Link to={`/news/${voicesCreatedSlug}`} className="badge-pill">
                View latest Industry Voices post
              </Link>
            )}
          </div>

          {voicesStatus && (
            <p className="secondary-text" style={{ marginTop: 10 }}>
              {voicesStatus}
            </p>
          )}
        </div>
      </details>

      <div style={{ height: 16 }} />

      <details className="admin-collapsible" open>
        <summary className="admin-collapsible-summary">
          <span>Slots review creation</span>
          <span className="secondary-text">Rewrite and save a slot review</span>
        </summary>
        <div className="admin-collapsible-body">
          <AdminSlotReviewTool />
        </div>
      </details>
    </div>
  );
}

export default AdminContentCreation;


